import { useEffect, useRef, useState } from "react";
import {
    Stack,
    Spinner,
    PrimaryButton,
    Label,
    MessageBar,
    MessageBarType,
    ProgressIndicator
} from "@fluentui/react";
import { deleteFile } from "../../../api/admin/api_admin";
import { FileInfo } from "../../../api/admin/models_admin";
import { displayFileName, formatFileSize } from "../components";
import { ApplyProgressRow, runSequentially } from "./apply_common";

export interface DeleteResult {
    deletedFileSeq: number[]
    failedFiles: FileInfo[]
}

/**
 * 「編集を完了」で保留していた削除をまとめて実行するペイン。
 * 表示されたら即座に削除を開始し、完了したらonCompleteで次のフェーズへ進む。
 * 削除の確認は呼び出し側(ApplyChangesModal)で済ませてある。
 */
export const DeleteFilePane = (props: {
    files: FileInfo[]
    onComplete: (result: DeleteResult) => void
}) => {
    const { files, onComplete } = props

    const [deleting, setDeleting] = useState<boolean>(true)
    // 削除の成否をファイルのseq単位で保持する。件数が進捗の分子になる
    const [deleteResults, setDeleteResults] = useState<Map<number, boolean>>(new Map())
    const [failedFiles, setFailedFiles] = useState<FileInfo[]>([])

    // React.StrictMode下ではeffectが2回実行されるため、二重に削除しないよう番人を置く
    const startedRef = useRef(false)

    useEffect(() => {
        if (startedRef.current) return
        startedRef.current = true
        runDelete()
    }, [])

    const runDelete = async () => {
        const { succeeded, failed } = await runSequentially(
            files,
            file => file.seq,
            async file => (await deleteFile(file.seq, file.rag_index)).success,
            setDeleteResults
        )
        setDeleting(false)
        setFailedFiles(failed)

        // 失敗があった場合はどのファイルが残ったかを見せるため、次へ進むのはユーザーに任せる
        if (failed.length === 0) {
            onComplete({ deletedFileSeq: succeeded.map(file => file.seq), failedFiles: failed })
        }
    }

    return (
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { flex: 1, minHeight: 0 } }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                {deleting && <Spinner />}
                <Label styles={{ root: { fontSize: 14, fontWeight: 700 } }}>
                    ファイル削除（{files.length}件）
                </Label>
            </Stack>

            {!deleting && 0 < failedFiles.length && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {failedFiles.length}件のファイルを削除できませんでした。
                    エラー表示のファイルは削除されていません。
                </MessageBar>
            )}

            {/* 削除対象のファイル一覧 */}
            <Stack style={{ overflow: 'auto', flex: 1 }} tokens={{ childrenGap: 6 }}>
                {files.map(file => (
                    <ApplyProgressRow
                        key={file.seq}
                        name={displayFileName(file)}
                        detail={formatFileSize(file.size)}
                        result={deleteResults.get(file.seq)}
                    />
                ))}
            </Stack>

            {/* 削除の進捗 */}
            <ProgressIndicator
                label={deleting ? "削除中..." : "削除完了"}
                description={`${deleteResults.size} / ${files.length}件`}
                percentComplete={0 < files.length ? deleteResults.size / files.length : 1}
            />

            {!deleting && 0 < failedFiles.length && (
                <Stack horizontalAlign="end" horizontal tokens={{ childrenGap: "10px" }}>
                    <PrimaryButton
                        text="続行する"
                        onClick={() => onComplete({
                            deletedFileSeq: [...deleteResults].filter(([, success]) => success).map(([seq]) => seq),
                            failedFiles
                        })}
                    />
                </Stack>
            )}
        </Stack>
    )
}
